// Parent Homework "All homework" tab (SC-H1b, design §2.2).
// Active/Past sub-tabs (reuses existing hw_active / hw3_past_homework keys), legend, specialist
// groups, per-homework cards (title, meta, today pill, day strip, exercise list). Card tap → board.
// Pure render from already-fetched data — no network, no signing (strips/ticks are text/box only).

import { isExerciseScheduledOn, exerciseSlotsOn } from '../data.js';
import { _generateDayBoxWindow, _findNextAppointment, _isHomeworkPast } from '../parent-view.js';
import {
  _fmtLocal, _countOn, _tintFor, _artFor, _dotColor, _dose, _recurrenceLabel, _isSlotDone, _esc,
} from './today.js';
import { buildTabRow } from './today.js';
import { buildReminderRow } from './hw-reminders.js';
import { printHomeworkSheet } from './print-sheet.js';

const T = (k, p) => window.HUD?.T?.(k, p) || k;

// Duration/end label for a card's meta line — reuses schedule-summary keys.
function _endsLabel(hw) {
  if (hw.duration_type === 'next_appointment') return T('hw2_sch_sum_until_session');
  if (hw.duration_type === 'end_date' && hw.end_date) return T('hw2_sch_sum_until_date', { date: hw.end_date });
  return T('hw2_sch_sum_ongoing');
}

// Day strip for one homework. Uses _generateDayBoxWindow's span; per box the per-slot count
// (same key format as Today, so ⭐/k-n/open agree) decides the state; gaps in the span show as
// "not scheduled" (dashed). Chronological order; RTL is handled by the flex container's dir.
export function dayStripHtml(hw, compMap, today, nextApptDate) {
  const boxes = _generateDayBoxWindow(hw, today, nextApptDate);
  if (!boxes.length) return '';
  const assigned = new Set(boxes.map(b => b.dStr));
  const todayStr = _fmtLocal(today);
  const letters = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
  let cells = '';
  const cur = new Date(boxes[0].date);
  const end = new Date(boxes[boxes.length - 1].date);
  while (cur <= end) {
    const dStr = _fmtLocal(cur);
    const num = cur.getDate();
    let cls = 'hwp-db', inner = `<span>${letters[cur.getDay()]}</span><b>${num}</b>`;
    if (dStr === todayStr) cls += ' today';
    if (!assigned.has(dStr)) {
      cls += ' na';
    } else {
      const c = _countOn([hw], compMap, new Date(cur));
      if (c.total > 0 && c.done === c.total) { cls += ' done'; inner = `<span class="s">⭐</span><b>${num}</b>`; }
      else if (c.done > 0) { cls += ' part'; inner = `<span class="s">${c.done}/${c.total}</span><b>${num}</b>`; }
      else if (dStr > todayStr) { cls += ' fut'; } // future open — faded; past/today open stay plain
    }
    cells += `<div class="${cls}">${inner}</div>`;
    cur.setDate(cur.getDate() + 1);
  }
  return `<div class="hwp-strip">${cells}</div>`;
}

function _groupBySpec(homeworks, H) {
  const cache = H.DB?.specialists || [];
  const map = {};
  homeworks.forEach(hw => {
    const key = hw.specialist_id || 'unknown';
    if (!map[key]) {
      const c = cache.find(s => s.id === key);
      map[key] = { specId: key, name: hw.specialist_name || c?.name || 'Specialist', role: c?.role || '', homeworks: [] };
    }
    map[key].homeworks.push(hw);
  });
  return Object.values(map);
}

function _card(hw, compMap, today, ctx) {
  const H = ctx.H;
  const todayStr = _fmtLocal(today);
  const cnt = _countOn([hw], compMap, today);
  const due = cnt.total > 0;
  const nEx = (hw.exercises || []).length;
  const meta = T(nEx === 1 ? 'hw3_n_ex' : 'hw3_n_ex_plural', { n: nEx }) + ' · ' + _recurrenceLabel(hw) + ' · ' + _endsLabel(hw);

  let pill;
  if (!due) pill = `<span class="hwp-pill off">${T('hw5_not_today')}</span>`;
  else if (cnt.done === cnt.total) pill = `<span class="hwp-pill">${T('hw5_done_today')}</span>`;
  else if (cnt.done > 0) pill = `<span class="hwp-pill amber">${T('hw5_k_of_n_today', { k: cnt.done, n: cnt.total })}</span>`;
  else pill = `<span class="hwp-pill">${T('hw5_n_today', { n: cnt.total })}</span>`;

  const nextAppt = hw.duration_type === 'next_appointment' ? _findNextAppointment(ctx.childId, hw.specialist_id, H) : null;
  const strip = dayStripHtml(hw, compMap, today, nextAppt);

  const exList = (hw.exercises || []).map(ex => {
    const slots = exerciseSlotsOn(hw, ex, today);
    const doneToday = due && slots.length > 0 && slots.every(s => _isSlotDone(compMap, ex.id, todayStr, s));
    return `<div><i class="${doneToday ? 'on' : ''}">✓</i><span class="hwp-ex-t">${_esc(ex.title || T('hw3_exercise'))}</span> <span class="hwp-ex-dose">· ${_esc(_dose(ex))}</span></div>`;
  }).join('');

  const card = document.createElement('div');
  card.className = 'hwp-hw' + (due ? ' today' : '');
  card.innerHTML = `<div class="hwp-hd"><div><h3 class="ff">${_esc(hw.title)}</h3><p class="hwp-meta">${_esc(meta)}</p></div>${pill}</div>${strip}<div class="hwp-ex">${exList}</div>`;
  card.onclick = () => ctx.onOpenBoard(hw.id);
  // Per-homework push-reminder control (SC2). Its own click handling stops propagation so tapping
  // the reminder row/panel never opens the board.
  const rem = buildReminderRow(hw, H);
  if (rem) card.appendChild(rem);
  return card;
}

// Quiet past cards — reuses _isHomeworkPast (the existing past-homework data path). Done summary is
// scheduled-days-done over the homework's active span within the loaded 90-day window.
function _daysSummary(hw, compMap) {
  const today = new Date(); today.setHours(0, 0, 0, 0);
  let from = new Date(hw.created_at || Date.now()); from.setHours(0, 0, 0, 0);
  const ninety = new Date(today); ninety.setDate(ninety.getDate() - 90);
  if (from < ninety) from = ninety;
  let to = today;
  if (hw.duration_type === 'end_date' && hw.end_date) { const e = new Date(hw.end_date + 'T00:00:00'); e.setHours(0, 0, 0, 0); if (e < to) to = e; }
  let totalDays = 0, doneDays = 0;
  const cur = new Date(from);
  while (cur <= to) {
    const c = _countOn([hw], compMap, new Date(cur));
    if (c.total > 0) { totalDays++; if (c.done === c.total) doneDays++; }
    cur.setDate(cur.getDate() + 1);
  }
  return { doneDays, totalDays };
}

function _renderPastInto(wrap, past, compMap, H) {
  if (!past.length) {
    const e = document.createElement('div'); e.className = 'hwp-grp'; e.textContent = T('hw3_no_past'); wrap.appendChild(e); return;
  }
  const cache = H.DB?.specialists || [];
  past.forEach(hw => {
    const specName = hw.specialist_name || cache.find(s => s.id === hw.specialist_id)?.name || 'Specialist';
    const s = _daysSummary(hw, compMap);
    const summary = s.totalDays ? T('hw3_done_of_total', { done: s.doneDays, total: s.totalDays }) : '';
    const card = document.createElement('div');
    card.className = 'hwp-hw hwp-past';
    card.innerHTML = `<div class="hwp-hd"><div><h3 class="ff">${_esc(hw.title)}</h3>` +
      `<p class="hwp-meta">${_esc(_recurrenceLabel(hw))} · ${_esc(specName)}</p></div>` +
      `<span class="hwp-pill off">${_esc(summary)}</span></div>`;
    wrap.appendChild(card);
  });
}

// ctx: { homeworks, compMap, childId, H, todayCount, allCount, allSubtab, onGoToday, onSetAllSubtab, onOpenBoard }
export function renderAllHomeworkInto(wrap, ctx) {
  const { homeworks, compMap, H } = ctx;
  const today = new Date(); today.setHours(0, 0, 0, 0);

  // Top tab row (Today | All homework), All active.
  wrap.appendChild(buildTabRow('all', ctx.todayCount, ctx.allCount, ctx.onGoToday, () => {}));

  const active = homeworks.filter(hw => !hw.is_paused && !_isHomeworkPast(hw, today, H));
  const past = homeworks.filter(hw => !hw.is_paused && _isHomeworkPast(hw, today, H));
  const sub = (ctx.allSubtab === 'past' && past.length) ? 'past' : 'active';

  // Active / Past sub-tabs — existing translated keys (hw_active / hw3_past_homework).
  const subRow = document.createElement('div');
  subRow.className = 'hwp-tabs';
  subRow.innerHTML =
    `<button class="hwp-tab sm ${sub === 'active' ? 'on' : ''}" data-s="active">${T('hw_active')} <span class="n">${active.length}</span></button>` +
    `<button class="hwp-tab sm ${sub === 'past' ? 'on' : ''}" data-s="past">${T('hw3_past_homework')} <span class="n">${past.length}</span></button>`;
  subRow.querySelector('[data-s="active"]').onclick = () => { if (sub !== 'active') ctx.onSetAllSubtab('active'); };
  subRow.querySelector('[data-s="past"]').onclick = () => { if (sub !== 'past') ctx.onSetAllSubtab('past'); };
  // Track B — Print practice sheet (week scope): a chip at the inline-END of the control row (mirrors
  // correctly in RTL). Static import → window.open runs in the click gesture (iOS Safari).
  if (homeworks.some(hw => !hw.is_paused && hw.status === 'active' && (hw.exercises || []).length)) {
    const pb = document.createElement('button');
    pb.className = 'hwp-print-chip';
    pb.title = T('hwp_print');
    pb.innerHTML = '🖨 <span>' + _esc(T('hwp_print')) + '</span>';
    pb.onclick = () => printHomeworkSheet(homeworks, ctx.childId, H, 'week');
    subRow.appendChild(pb);
  }
  wrap.appendChild(subRow);

  if (sub === 'past') { _renderPastInto(wrap, past, compMap, H); return; }

  // Legend
  const legend = document.createElement('div');
  legend.className = 'hwp-legend';
  legend.innerHTML =
    `<span>⭐ ${T('hw5_legend_all')}</span><span>1/2 ${T('hw5_legend_part')}</span>` +
    `<span>▢ ${T('hw5_legend_open')}</span><span class="hwp-legend-na">⋯ ${T('hw5_legend_na')}</span>`;
  wrap.appendChild(legend);

  if (!active.length) {
    const e = document.createElement('div'); e.className = 'hwp-grp'; e.textContent = T('hw3_no_active'); wrap.appendChild(e); return;
  }

  _groupBySpec(active, H).forEach(g => {
    const gh = document.createElement('div');
    gh.className = 'hwp-spec-h';
    gh.innerHTML = `<i class="hwp-dot" style="background:${_dotColor(g.specId)}"></i>${_esc(g.name)} ` +
      `<small>${_esc(g.role)}</small><span class="cnt">${T('hw5_n_active', { n: g.homeworks.length })}</span>`;
    wrap.appendChild(gh);
    g.homeworks.forEach(hw => wrap.appendChild(_card(hw, compMap, today, ctx)));
  });
}
